import {AGENCY_KEYS} from "@/api/agencyApi";
import {COMPANY_KEYS, useCurrentCompanyId} from "@/api/companyApi";
import {PAYMENT_KEYS} from "@/api/paymentApi";
import {USER_KEYS} from "@/api/userApi";
import type {InvoiceSerializer, PackageSubscriptionSerializer} from "@/generated/DjangoTypes";
import {useFetchUser} from "@/hooks/useFetchUser";
import {useSelectedCompany} from "@/hooks/useUserHooks";
import {client} from "@/httpClient";
import paymentErrorsDe from "@/locale/ger/payment_errors.json";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import type {TPackageName, TPackagePaymentPeriodType} from "@/store/types";
import useUserStore from "@/store/user";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import dayjs from "dayjs";
import {useTranslation} from "react-i18next";

const PAYMENT_GRACE_PERIOD_DAYS = 10;

const getPaymentDeadline = (invoices: InvoiceSerializer[]) =>
  invoices.length
    ? dayjs(Math.min(...invoices.map(invoice => dayjs(invoice.created).valueOf()))).add(
        PAYMENT_GRACE_PERIOD_DAYS,
        "days",
      )
    : null;

const usePayment = (scope?: {agencyId?: number; companyId?: number}) => {
  const {t} = useTranslation(["common", "payment_errors"]);
  const {refetch} = useFetchUser();
  const queryClient = useQueryClient();
  const currentCompanyId = useCurrentCompanyId();
  const selectedCompany = useSelectedCompany();

  const agencyId = scope?.agencyId ?? selectedCompany?.agency ?? undefined;
  const companyId = scope?.companyId ?? currentCompanyId ?? 0;
  const paymentKeys =
    agencyId !== undefined && !Number.isNaN(agencyId)
      ? {
          attach: AGENCY_KEYS.mAgencyPaymentMethodAttach(agencyId),
          detach: AGENCY_KEYS.mAgencyPaymentMethodDetach(agencyId),
          setDefault: AGENCY_KEYS.mAgencyPaymentMethodDefault(agencyId),
          setupIntent: AGENCY_KEYS.mAgencyCreateSetupIntent(agencyId),
          query: AGENCY_KEYS.qAgencyPaymentMethods(agencyId),
        }
      : null;

  const getTranslatedErrorMessage = (invoice: InvoiceSerializer) => {
    if (!invoice.failure_code) return "";

    if (Object.keys(paymentErrorsDe).includes(invoice.failure_code))
      return t(`payment_errors:${invoice.failure_code as "card_declined"}`);

    return invoice.failure_message;
  };

  const {mutateAsync: purchasePlan} = useMutation({
    mutationKey: PAYMENT_KEYS.mPackageListSubscribe,
    mutationFn: ({
      packageId,
      packageName,
      packageType,
      successUrl: customSuccessUrl,
    }: {
      packageId: number;
      packageName?: TPackageName;
      packageType: TPackagePaymentPeriodType;
      successUrl?: string;
    }) => {
      const searchParams = new URLSearchParams(window.location.search);
      const campaignId = searchParams.get("campaign_id");
      const campaignType = searchParams.get("type");
      const successUrl =
        customSuccessUrl ??
        (campaignType
          ? `${window.location.origin}/campaign/${campaignId}/edit?isUgc=false&step=3`
          : `${window.location.origin}/welcome/${packageName ? `?plan=${packageName}` : ""}`);

      return client.post<PackageSubscriptionSerializer & {stripe_url?: string}>(`/packages/${packageId}/subscribe/`, {
        package_type: packageType,
        success_url: successUrl,
        cancel_url: window.location.href,
      });
    },
    onSuccess: response => {
      capturePostHogEvent(PostHogEvents.PackageSubscriptionCreated, {subscriptionId: response.data.id});

      if (response.data.stripe_url) {
        window.location.href = response.data.stripe_url;
      }
    },
  });

  const {mutateAsync: upgradePlan, isPending: upgradePlanIsLoading} = useMutation({
    mutationKey: PAYMENT_KEYS.mPackageSubscriptionUpgrade,
    mutationFn: ({
      package_type,
      package_id,
      subscriptionId,
    }: {
      package_type: TPackagePaymentPeriodType;
      package_id: number;
      subscriptionId: number;
    }) =>
      client.post<PackageSubscriptionSerializer>(`/package_subscriptions/${subscriptionId}/upgrade/`, {
        package_type,
        package_id,
      }),
    onSuccess: async response => {
      capturePostHogEvent(PostHogEvents.PackageSubscriptionUpgraded, {subscriptionId: response.data.id});
      await refetch();

      if (companyId) {
        queryClient.invalidateQueries({queryKey: COMPANY_KEYS.qCompany(companyId)});
      }
    },
  });

  const {mutateAsync: payInvoices, isPending: invoicePayIsLoading} = useMutation({
    mutationKey: PAYMENT_KEYS.mInvoiceBatchPayment,
    mutationFn: ({invoiceIds}: {invoiceIds: number[]}) =>
      client.post(PAYMENT_KEYS.mInvoiceBatchPayment[0], {invoice_ids: invoiceIds}),
    onSuccess: response => {
      const userId = Number(useUserStore.getState().user.id);

      if (response.data.stripe_url) {
        window.location.href = response.data.stripe_url;
      }

      setTimeout(() => {
        queryClient.invalidateQueries({queryKey: USER_KEYS.qUser(userId)});
      }, 2000);
    },
  });

  const {mutateAsync: createSetupIntent, isPending: createSetupIntentLoading} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: paymentKeys?.setupIntent ?? ["agency-payment-methods-unresolved"],
    mutationFn: async () => {
      if (!paymentKeys) throw new Error("agency_not_resolved");
      const response = await client.post<{client_secret: string}>(paymentKeys.setupIntent[0], {});
      return response.data.client_secret;
    },
  });

  const {mutateAsync: attachPaymentMethod, isPending: attachLoading} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: paymentKeys?.attach ?? ["agency-payment-methods-unresolved"],
    mutationFn: ({paymentMethodId}: {paymentMethodId: string}) => {
      if (!paymentKeys) throw new Error("agency_not_resolved");
      return client.post(paymentKeys.attach[0], {payment_method_id: paymentMethodId});
    },
    onSuccess: () => {
      if (paymentKeys) queryClient.invalidateQueries({queryKey: paymentKeys.query});
    },
  });

  const {mutateAsync: detachPaymentMethod, isPending: detachLoading} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: paymentKeys?.detach ?? ["agency-payment-methods-unresolved"],
    mutationFn: ({paymentMethodId}: {paymentMethodId: string}) => {
      if (!paymentKeys) throw new Error("agency_not_resolved");
      return client.post(paymentKeys.detach[0], {payment_method_id: paymentMethodId});
    },
    onSuccess: () => {
      if (paymentKeys) queryClient.invalidateQueries({queryKey: paymentKeys.query});
    },
  });

  const {mutateAsync: setDefaultPaymentMethod, isPending: setDefaultLoading} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: paymentKeys?.setDefault ?? ["agency-payment-methods-unresolved"],
    mutationFn: ({paymentMethodId}: {paymentMethodId: string}) => {
      if (!paymentKeys) throw new Error("agency_not_resolved");
      return client.post(paymentKeys.setDefault[0], {payment_method_id: paymentMethodId});
    },
    onSuccess: () => {
      if (paymentKeys) queryClient.invalidateQueries({queryKey: paymentKeys.query});
    },
  });

  return {
    purchasePlan,
    upgradePlan,
    upgradePlanIsLoading,
    payInvoices,
    invoicePayIsLoading,
    createSetupIntent,
    createSetupIntentLoading,
    attachPaymentMethod,
    attachLoading,
    detachPaymentMethod,
    detachLoading,
    setDefaultPaymentMethod,
    setDefaultLoading,
    getTranslatedErrorMessage,
  };
};

export {getPaymentDeadline, usePayment};
